import { BannerRequestType, BannerType, PermissionsType } from "../../../../types/types";
import { AttachFileOutlined, CampaignOutlined, CheckCircleRounded, CloseOutlined, DoNotDisturbOnRounded, DriveFileRenameOutlineOutlined, EventBusyOutlined, HelpOutlineOutlined, KeyOutlined, RemoveCircleOutlineOutlined, SaveOutlined, SubjectOutlined } from "@mui/icons-material";
import { Box, Button, Chip, FormControl, Grid, InputLabel, Paper, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { useCookies } from "react-cookie";
import { LocalizationProvider, MobileDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import GridTemplate from "../../../../components/GridTemplate";
import Rmodal from "../../../../components/Rmodal";
import { getFilteredList, setToastRefToOpen } from "../../../../functions/functions";
import bannerFindAllCrudResponse from "../../../../resources/Api/banner/bannerFindAllCrudResponse";
import bannerOnSaveData from "../../../../resources/Api/banner/bannerOnSaveData";
import bannerOnUpdateData from "../../../../resources/Api/banner/bannerOnUpdateData";
import bannerUpdateStatusById from "../../../../resources/Api/banner/bannerUpdateStatusById";

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 130,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Ativo"}
                icon={<KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded className="activated" style={{ height: 28, width: 28 }} />
                    ) : (
                        <DoNotDisturbOnRounded className="deactivated" style={{ height: 28, width: 28 }} />
                    )
                }
            />
        ),
    },
    {
        field: "title",
        align: "left",
        width: 260,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Título"}
                icon={<CampaignOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={item.row.title} />
        ),
    },
    {
        field: "expiresAt",
        align: "left",
        width: 220,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Expira em"}
                icon={<EventBusyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent", fontFamily: "Prometo" }}
                label={
                    item.row.expiresAt && dayjs(item.row.expiresAt).isValid()
                        ? dayjs(item.row.expiresAt).format("DD/MM/YYYY HH:mm")
                        : "Sem expiração"
                }
            />
        ),
    },
    {
        field: "message",
        align: "left",
        flex: 1,
        minWidth: 220,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Mensagem"}
                icon={<SubjectOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={item.row.message || "-"} />
        ),
    },
];

/**
 * BannerComponent - Tab de "Avisos" dentro do Suporte ao cliente: lista, cadastra e gerencia os banners do app
 * @component
 */
const BannerComponent = ({ permission }: { permission?: PermissionsType }) => {
    // functionalities states
    const [loading, setLoading] = useState(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<BannerType>>([]);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    // states
    const [bannersList, setBannersList] = useState<Array<BannerType>>([]);
    const [selectedBanner, setSelectedBanner] = useState<BannerType>({ id: -1 } as any);

    const [bannerTitle, setBannerTitle] = useState<string>("");
    const [bannerMessage, setBannerMessage] = useState<string>("");
    const [bannerExpiresAt, setBannerExpiresAt] = useState<string | null>(null);

    const [bannerImage, setBannerImage] = useState<File | null>(null);
    const [bannerImagePreview, setBannerImagePreview] = useState<string>("");

    // refs
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);

    // cookies
    const [cookies] = useCookies(["selectedParameters", "token"]);

    // hooks e const
    const canCreate = permission?.canCreate === true;
    const canEdit = permission?.canEdit === true;

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setInEdit(false);
        setInInsert(false);
        setSelectedBanner({} as any);
        setBannerTitle("");
        setBannerMessage("");
        setBannerExpiresAt(null);
        setBannerImage(null);
        setBannerImagePreview("");
    }, []);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(ref, validationText, "Atenção!", statusMessage, undefined, 3000, false);
        }
    }, [isOpenDetails, validationText, isFieldsValid, statusMessage, isToastVisible]);

    useEffect(() => {
        if (bannerImage) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setBannerImagePreview(reader.result as string);
            };
            reader.readAsDataURL(bannerImage);
        } else {
            setBannerImagePreview("");
        }
    }, [bannerImage]);

    const getAllBanners = useCallback(
        async (status: string = "activated", page: number = 0, size: number = 100) => {
            const token: string = cookies.token || sessionStorage.getItem("token");

            setLoading(true);
            try {
                const { data } = await bannerFindAllCrudResponse(token, status, page, size);
                setBannersList(data.content);
                setIsLastPage(data.last);
                setNumberOfElementsPerPage(data.numberOfElements);
                setPageNumber(data.pageable.pageNumber);

                if (data.first) {
                    setNumberOfElements(data.totalElements);
                }
            } catch (error: any) {
                console.log(error);
                setBannersList([]);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            }
        },
        [cookies.token]
    );

    useEffect(() => {
        getAllBanners();
    }, [getAllBanners]);

    useEffect(() => {
        setFilteredList(getFilteredList(["title", "message"], bannersList, query));
    }, [bannersList, query]);

    const onSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const data: BannerRequestType = {
            id: -1,
            title: bannerTitle,
            message: bannerMessage || null,
            image: bannerImagePreview || null,
            expiresAt: bannerExpiresAt,
        };

        if (!data.title || !data.image) {
            setTextValidation("Título e imagem são obrigatórios, preencha e tente novamente.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await bannerOnSaveData(token, data);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllBanners();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [cookies.token, bannerTitle, bannerMessage, bannerImagePreview, bannerExpiresAt, handleClose, getAllBanners]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const data: BannerRequestType = {
            id: selectedBanner.id,
            title: bannerTitle,
            message: bannerMessage || null,
            image: bannerImagePreview || null,
            expiresAt: bannerExpiresAt,
        };

        if (!data.title) {
            setTextValidation("Título é obrigatório, preencha e tente novamente.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (
            data.title === selectedBanner.title &&
            (data.message ?? null) === (selectedBanner.message ?? null) &&
            (data.expiresAt ?? null) === (selectedBanner.expiresAt ?? null) &&
            !bannerImagePreview
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await bannerOnUpdateData(token, data, data.id);
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllBanners();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [cookies.token, selectedBanner, bannerTitle, bannerMessage, bannerImagePreview, bannerExpiresAt, handleClose, getAllBanners]);

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                await bannerUpdateStatusById(token, id, status);
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response;

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllBanners();
                }, 8000);
            }
        },
        [cookies.token, getAllBanners, handleClose]
    );

    const handleOnEditValues = useCallback((banner: BannerType) => {
        if (banner?.title && !inInsert) {
            setBannerTitle(banner.title);
            setBannerMessage(banner.message ?? "");
            setBannerExpiresAt(banner.expiresAt ?? null);
        }
    }, [inInsert]);

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid className="container" sx={{ height: 500, width: 700 }}>
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 500, padding: 5 }}>
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                hiddenBackdrop={false}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);
                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper sx={style}>
                    <Box className="row" style={{ background: "#ffff", boxShadow: "none", padding: 10 }}>
                        {!loading ? (
                            <>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        {inInsert ? "Novo banner" : "Banner"}
                                    </Typography>
                                </Box>
                                <Box className="col-sm-12 col-md-5" style={{ display: "flex", justifyContent: "end" }}>
                                    {selectedBanner?.title && (
                                        <>
                                            <Chip
                                                style={{
                                                    color: selectedBanner.activated ? "#00B700" : "#dc3545",
                                                    marginRight: 5,
                                                    fontWeight: "bolder",
                                                }}
                                                clickable={false}
                                                onClick={() => { }}
                                                label={selectedBanner.activated ? "Ativo" : "Inativo"}
                                            />
                                            {selectedBanner.activated && (
                                                <Chip
                                                    style={{ color: "black", marginRight: 5 }}
                                                    disabled={!canEdit}
                                                    onClick={() => setInEdit(true)}
                                                    label="Editar"
                                                    icon={<DriveFileRenameOutlineOutlined style={{ height: 24, width: 24, fill: "black" }} />}
                                                />
                                            )}
                                            <Chip
                                                style={{ color: "black", marginRight: 5 }}
                                                disabled={!canEdit}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        onUpdateStatus(selectedBanner.id, selectedBanner.activated ? "deactivate" : "activate");
                                                    }
                                                }}
                                                label={selectedBanner.activated ? "Desativar" : "Ativar"}
                                                icon={<RemoveCircleOutlineOutlined style={{ height: 24, width: 24, fill: "black" }} />}
                                            />
                                        </>
                                    )}
                                </Box>

                                <Box className="row">
                                    <Box className="col-md-12" style={{ margin: "15px 0px 15px 0px" }}>
                                        <FormControl className="form-group" style={{ display: "flex", width: "100%" }}>
                                            <InputLabel
                                                shrink
                                                disabled={(selectedBanner?.title && !inEdit && !inInsert) as boolean}
                                                style={{ fontFamily: "Prometo" }}
                                                required
                                            >
                                                Título
                                            </InputLabel>
                                            <TextField
                                                name="title"
                                                InputProps={{ label: "Título", notched: true, style: { fontFamily: "Prometo" } }}
                                                disabled={(selectedBanner?.title && !inEdit && !inInsert) as boolean}
                                                value={bannerTitle}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => setBannerTitle(event.target.value)}
                                                type="text"
                                                autoComplete="off"
                                                className="form-control form-control-login"
                                                placeholder="Digite o título do banner"
                                                required
                                            />
                                        </FormControl>
                                    </Box>

                                    <Box className="col-md-12" style={{ margin: "15px 0px 15px 0px" }}>
                                        <FormControl className="form-group" style={{ display: "flex", width: "100%" }}>
                                            <InputLabel
                                                shrink
                                                disabled={(selectedBanner?.title && !inEdit && !inInsert) as boolean}
                                                style={{ fontFamily: "Prometo", display: "flex", alignItems: "center", gap: 4 }}
                                            >
                                                Mensagem
                                                <Tooltip title="Texto opcional exibido como dica ao tocar no banner dentro do aplicativo — não fica fixo sobre a imagem.">
                                                    <HelpOutlineOutlined sx={{ fontSize: 18, color: "text.secondary", cursor: "help" }} />
                                                </Tooltip>
                                            </InputLabel>
                                            <TextField
                                                name="message"
                                                multiline
                                                minRows={2}
                                                InputProps={{ label: "Mensagem", notched: true, style: { fontFamily: "Prometo" } }}
                                                disabled={(selectedBanner?.title && !inEdit && !inInsert) as boolean}
                                                value={bannerMessage}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => setBannerMessage(event.target.value)}
                                                autoComplete="off"
                                                className="form-control form-control-login"
                                                placeholder="Mensagem opcional exibida como dica"
                                            />
                                        </FormControl>
                                    </Box>

                                    <Box className="col-md-12" style={{ margin: "15px 0px 15px 0px" }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                            <MobileDateTimePicker
                                                sx={{ width: "100%" }}
                                                label="Data e hora de expiração (opcional)"
                                                format="DD/MM/YYYY HH:mm"
                                                disabled={(selectedBanner?.title && !inEdit && !inInsert) as boolean}
                                                value={bannerExpiresAt && dayjs(bannerExpiresAt).isValid() ? dayjs(bannerExpiresAt) : null}
                                                onChange={(value) => {
                                                    setBannerExpiresAt(
                                                        value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DDTHH:mm:ss") : null
                                                    );
                                                }}
                                                slotProps={{
                                                    textField: { InputLabelProps: { shrink: true }, placeholder: "Sem expiração" },
                                                    field: { clearable: true } as any,
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>

                                    <Box className="col-md-12" style={{ margin: "15px 0px 15px 0px" }}>
                                        <InputLabel shrink style={{ fontFamily: "Prometo" }} required>
                                            Imagem
                                        </InputLabel>
                                        <input
                                            type="file"
                                            style={{ display: "none" }}
                                            ref={fileInputRef}
                                            accept="image/*"
                                            disabled={(selectedBanner?.title && !inEdit && !inInsert) as boolean}
                                            onChange={(event) => {
                                                const file = event.target.files;
                                                if (file && file[0]?.type.substring(0, 5) === "image") {
                                                    setBannerImage(file[0]);
                                                } else {
                                                    setBannerImage(null);
                                                }
                                            }}
                                        />
                                        {(bannerImagePreview || selectedBanner?.image) ? (
                                            <Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
                                                <img
                                                    src={bannerImagePreview || selectedBanner?.image}
                                                    alt="preview"
                                                    style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 8 }}
                                                />
                                                {(inEdit || inInsert) && (
                                                    <Chip
                                                        label="Trocar imagem"
                                                        icon={<AttachFileOutlined />}
                                                        onClick={() => fileInputRef.current?.click()}
                                                    />
                                                )}
                                            </Box>
                                        ) : (
                                            <Chip
                                                label="Anexar imagem"
                                                icon={<AttachFileOutlined />}
                                                disabled={!(inEdit || inInsert)}
                                                onClick={() => fileInputRef.current?.click()}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            loadingEsqueleton
                        )}

                        <Paper
                            style={{
                                background: "transparent",
                                boxShadow: "none",
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: 10,
                            }}
                        >
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff", marginRight: 5 }}
                                onClick={() => {
                                    if (!isToastVisible) handleClose();
                                }}
                                className="btn btn-primary btn-block login-button-close"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography className="forgot-password" style={{ fontFamily: "Prometo", fontSize: "16px", color: "#ffff" }}>
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={!inEdit && !inInsert}
                                onClick={() => (inInsert && !inEdit ? onSave() : onUpdate())}
                                className="btn btn-primary btn-block login-button"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography className="forgot-password" style={{ fontFamily: "Prometo", fontSize: "16px", color: "#ffff" }}>
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        isOpenDetails,
        loading,
        loadingEsqueleton,
        inInsert,
        inEdit,
        canEdit,
        selectedBanner,
        bannerTitle,
        bannerMessage,
        bannerExpiresAt,
        bannerImagePreview,
        isToastVisible,
        validationText,
        handleClose,
        onSave,
        onUpdate,
        onUpdateStatus,
    ]);

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);
                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName={"Avisos"}
                isHiddenMenu={false}
                isLoading={loading}
                order={"desc"}
                rows={filteredList}
                sortField="title"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Título ou mensagem"}
                onRefresh={getAllBanners}
                onExport={async () => { }}
                disableReportButton={true}
                disableAddButton={!canCreate}
                handleNext={(activated: string, page: number, size: number) => getAllBanners(activated, page, size)}
                handlePrevious={() => { }}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                contentHeight={"500px"}
                containerHeight="auto"
                style={{ width: "100%", marginTop: 0, padding: 0 }}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => setLoading(false), 2000);
                }}
                onClickRow={(item: any) => {
                    setIsOpenDetails(true);
                    setSelectedBanner(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => setLoading(false), 2000);
                }}
                withCustomerParameter={false}
            />
        </>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "#f3f4f6",
    border: "none",
    boxShadow: "none",
    borderRadius: 1,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 3,
    fontFamily: "Prometo",
};

export default BannerComponent;
